import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// Interface for partner data from database
export interface Partner {
  id: string;
  user_id: string;
  partner_type: 'co_branded' | 'white_label';
  first_login_completed: boolean;
  onboarding_completed: boolean;
  onboarding_progress?: Record<string, {
    completed: boolean;
    completedAt: string | null;
  }>;
}

// Hook to get current partner data
export function usePartners() {
  const { user } = useAuth();

  return useQuery<Partner | null>({
    queryKey: ['partners', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('partners')
        .select('id, user_id, first_login_completed, partner_type, onboarding_progress, onboarding_completed')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching partner data:', error);
        throw error;
      }

      return data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, 
    refetchOnWindowFocus: false,
  });
}

// Hook to update partner data
export function useUpdatePartner() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: Partial<Partner>) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('partners')
        .update(updates)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating partner:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      // Invalidate and refetch partner data
      queryClient.invalidateQueries({ queryKey: ['partners', user?.id] });
    },
    onError: (error) => {
      console.error('Failed to update partner:', error);
      toast.error('Failed to update partner settings');
    },
  });
}


